import { useState, useEffect } from 'react'

import api from '../../../api/api'
import { STORAGE_KEYS } from '../utils'
import { getFromStorage, setStorageValue } from '../../../helpers/helpers'

import { getPackageSessions } from './useOfflineMode'
import { SCREENS } from './usePackageRoom'

const SYNC_INTERVAL = 15 * 60 * 1000 // 15 minutes in milliseconds

const getLastSyncTimestamp = () => {
  return getFromStorage(STORAGE_KEYS.LAST_SYNC_TIMESTAMP, null)
}

const setLastSyncTimestamp = (timestamp) => {
  setStorageValue(STORAGE_KEYS.LAST_SYNC_TIMESTAMP, timestamp)
}

export const removePackagesSession = (sessionId) => {
  const packageSessions = getPackageSessions()
  setStorageValue(
    STORAGE_KEYS.PACKAGE_SESSIONS,
    packageSessions.filter((session) => session.id !== sessionId),
  )
}

export const useSynchroniseOfflinePackages = (
  isOffline,
  isBadConnection = false,
  currentScreen,
) => {
  const [isSyncing, setIsSyncing] = useState(false)

  const synchronizePackages = async () => {
    if (isOffline || isBadConnection || isSyncing || currentScreen !== SCREENS.HOME) return

    const packageSessions = getPackageSessions()
    if (packageSessions.length === 0) {
      setLastSyncTimestamp(Date.now())
      return
    }

    setIsSyncing(true)

    for (const packageSession of packageSessions) {
      const { id, packages, startTime, endTime } = packageSession
      try {
        await api.syncOfflinePackageSession({
          packages,
          startTime,
          endTime,
        })
        removePackagesSession(id)
      } catch (error) {
        // Note: If package synchronization failed remove the corrupted package session
        // to avoid infinite loop and make sure package room will work properly.
        if (error.response.status === 400) {
          removePackagesSession(id)
        }
        console.error('Error synchronizing packages:', error)
      }
    }

    setLastSyncTimestamp(Date.now())
    setIsSyncing(false)
  }

  useEffect(() => {
    const syncIfNeeded = () => {
      const now = Date.now()
      const lastSync = getLastSyncTimestamp() || 0

      if (now - lastSync >= SYNC_INTERVAL && !isOffline && !isBadConnection) {
        synchronizePackages()
      }
    }

    syncIfNeeded()
    const intervalId = setInterval(syncIfNeeded, SYNC_INTERVAL)

    return () => clearInterval(intervalId)
  }, [isOffline, isBadConnection, currentScreen])

  return { isSyncing }
}
